import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase-client';
import {
  disableWebPush,
  enableWebPush,
  getBrowserTimezone,
  getNotificationPermission,
  getWebPushReadiness,
  updatePushPreferences,
} from '../../utils/webPush';
import { reportCaughtError } from '../../utils/clientErrorReporting';
import {
  DEFAULT_NOTIFICATION_CATEGORY_IDS,
  NOTIFICATION_CATEGORIES,
  normalizeNotificationCategoryIds,
} from '../../utils/notificationPreferences';
import { offlineWriteMessage, useOnlineRefreshSignal, useOnlineStatus } from '../../utils/onlineStatus';
import { teamHasMetadataLabel } from '../teams/utils/teamMetadata';
import { usePwaInstallPrompt } from '../pwa/PwaInstallContext';
import SearchableSelectDropdown from './formComponents/SearchableSelectDropdown';
import './communicationPreferences.css';

const ROWING_CATEGORY_ID = 'rowing';
const FAMILY_PROFILE_CATEGORY_ID = 'family_profile';

const DEFAULT_PUSH_PREFS = {
  enabled: false,
  quietHours: {
    enabled: true,
    start: '22:00',
    end: '08:00',
  },
  timezone: '',
  categories: DEFAULT_NOTIFICATION_CATEGORY_IDS,
};

const DEFAULT_EMAIL_PREFS = {
  receiveEmail: true,
  frequency: 'instant',
  categories: DEFAULT_NOTIFICATION_CATEGORY_IDS,
};

function pushPreferenceErrorMessage(err, fallback) {
  const code = String(err?.code || '').toLowerCase();
  const message = String(err?.message || '').trim();
  if (code.includes('internal') || message.toLowerCase() === 'internal') {
    return 'Push notifications are not available right now. Please try again shortly.';
  }
  if (code.includes('unauthenticated')) {
    return 'Your session needs refreshing. Sign in again, then try enabling notifications.';
  }
  return message || fallback;
}

function isExpectedPushEnableFailure(err) {
  const code = String(err?.code || '').toLowerCase();
  return code === 'permission-denied'
    || code === 'permission-not-granted'
    || code === 'install-required'
    || code === 'ios-install-required'
    || code === 'unsupported'
    || code === 'development-build'
    || code === 'missing-vapid-key'
    || code === 'push-service-unavailable';
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Dublin',
  'Europe/Paris',
  'Europe/Berlin',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Toronto',
  'Australia/Sydney',
  'Australia/Melbourne',
  'Pacific/Auckland',
];

function getTimezoneOptions(...preferredZones) {
  const supportedZones = typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function'
    ? Intl.supportedValuesOf('timeZone')
    : FALLBACK_TIMEZONES;
  return Array.from(new Set([
    ...preferredZones.filter(Boolean),
    ...FALLBACK_TIMEZONES,
    ...supportedZones,
  ])).sort((a, b) => a.localeCompare(b));
}

function isRowingTeam(team = {}) {
  return teamHasMetadataLabel(team?.teamMetadata || team?.metadata || team, 'Rowing Club');
}

export default function CommunicationPreferencesForm({ userId }) {
  const [prefs, setPrefs] = useState(DEFAULT_EMAIL_PREFS);
  const [pushPrefs, setPushPrefs] = useState(DEFAULT_PUSH_PREFS);
  const [showRowingCategory, setShowRowingCategory] = useState(false);
  const [showFamilyProfileCategory, setShowFamilyProfileCategory] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [emailSaving, setEmailSaving] = useState(false);
  const [emailMessage, setEmailMessage] = useState('');
  const [pushSaving, setPushSaving] = useState(false);
  const [pushMessage, setPushMessage] = useState('');
  const online = useOnlineStatus();
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const { isIos, isStandalone } = usePwaInstallPrompt();
  const browserTimezone = getBrowserTimezone();
  const hasLoadedRef = useRef(false);
  const timezoneOptions = useMemo(
    () => getTimezoneOptions(pushPrefs.timezone, browserTimezone),
    [browserTimezone, pushPrefs.timezone]
  );
  const pushReadiness = getWebPushReadiness({ isIos, isStandalone });

  const fetchPrefs = useCallback(async ({ showLoader = false } = {}) => {
    if (showLoader || !hasLoadedRef.current) setLoading(true);
    setLoadError('');
    try {
      const [docSnap, userSnap] = await Promise.all([
        getDoc(doc(db, 'privateUsers', userId)),
        getDoc(doc(db, 'users', userId)),
      ]);
      const data = docSnap.exists() ? docSnap.data() : {};
      const userData = userSnap.exists() ? userSnap.data() : {};
      const teamIds = Object.keys(userData?.team_roles || {}).filter(Boolean);
      const familyIds = Object.keys(userData?.family_roles || {}).filter(Boolean);
      const teamSnaps = await Promise.all(
        teamIds.map((teamId) => getDoc(doc(db, 'teams', teamId)).catch(() => null))
      );
      setShowRowingCategory(teamSnaps.some((teamSnap) => (
        teamSnap?.exists?.() && isRowingTeam(teamSnap.data() || {})
      )));
      setShowFamilyProfileCategory(familyIds.length > 0);
      const emailPrefs = data?.communicationPreferences || null;
      const storedPushPrefs = data?.pushPreferences || {};
      setPrefs({
        receiveEmail: emailPrefs?.receiveEmail ?? true,
        frequency: emailPrefs?.frequency ?? 'instant',
        categories: normalizeNotificationCategoryIds(emailPrefs?.categories),
      });
      setPushPrefs({
        ...DEFAULT_PUSH_PREFS,
        ...storedPushPrefs,
        quietHours: {
          ...DEFAULT_PUSH_PREFS.quietHours,
          ...(storedPushPrefs.quietHours || {}),
        },
        timezone: storedPushPrefs.timezone || getBrowserTimezone(),
        categories: normalizeNotificationCategoryIds(storedPushPrefs.categories),
      });
      hasLoadedRef.current = true;
      setLoading(false);
    } catch (err) {
      setLoadError(err.message || 'Could not load communication preferences.');
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    hasLoadedRef.current = false;
    fetchPrefs({ showLoader: true });
  }, [fetchPrefs, userId]);

  useEffect(() => {
    if (!onlineRefreshSignal) return;
    fetchPrefs({ showLoader: false });
  }, [fetchPrefs, onlineRefreshSignal]);

  const handleSave = async () => {
    if (!online) {
      setEmailMessage(offlineWriteMessage('save communication preferences'));
      return;
    }
    setEmailSaving(true);
    setEmailMessage('');
    try {
      await setDoc(doc(db, 'privateUsers', userId), {
        communicationPreferences: {
          ...prefs,
          categories: normalizeNotificationCategoryIds(prefs.categories),
        },
      }, { merge: true });
      setEmailMessage('Email preferences saved.');
    } catch (err) {
      setEmailMessage(err.message || 'Could not save email preferences.');
    } finally {
      setEmailSaving(false);
    }
  };

  const toggleEmailCategory = (categoryId) => {
    setPrefs((prev) => {
      const categories = normalizeNotificationCategoryIds(prev.categories);
      const nextCategories = categories.includes(categoryId)
        ? categories.filter((id) => id !== categoryId)
        : [...categories, categoryId];
      return { ...prev, categories: nextCategories };
    });
  };

  const savePushPreferences = async (nextPrefs) => {
    const normalized = {
      ...pushPrefs,
      ...nextPrefs,
      quietHours: {
        ...pushPrefs.quietHours,
        ...(nextPrefs.quietHours || {}),
      },
      timezone: nextPrefs.timezone || pushPrefs.timezone || getBrowserTimezone(),
      categories: normalizeNotificationCategoryIds(nextPrefs.categories || pushPrefs.categories),
    };
    if (!online) {
      setPushMessage(offlineWriteMessage('save push preferences'));
      return;
    }
    setPushSaving(true);
    setPushMessage('');
    try {
      await updatePushPreferences(normalized);
      setPushPrefs(normalized);
      setPushMessage('Push preferences saved.');
    } catch (err) {
      setPushMessage(pushPreferenceErrorMessage(err, 'Could not save push preferences.'));
      reportCaughtError(err, {
        source: 'notifications.push.savePreferences',
        severity: 'warning',
        context: {
          isIos,
          isStandalone,
          notificationPermission: getNotificationPermission(),
        },
      });
    } finally {
      setPushSaving(false);
    }
  };

  const togglePushCategory = (categoryId) => {
    const categories = normalizeNotificationCategoryIds(pushPrefs.categories);
    const nextCategories = categories.includes(categoryId)
      ? categories.filter((id) => id !== categoryId)
      : [...categories, categoryId];
    savePushPreferences({ categories: nextCategories });
  };

  const handleEnablePush = async () => {
    if (!online) {
      setPushMessage(offlineWriteMessage('enable push notifications'));
      return;
    }
    setPushSaving(true);
    setPushMessage('');
    try {
      await enableWebPush({ isIos, isStandalone });
      const nextPrefs = {
        ...pushPrefs,
        enabled: true,
        timezone: pushPrefs.timezone || browserTimezone,
      };
      await updatePushPreferences(nextPrefs);
      setPushPrefs(nextPrefs);
      setPushMessage('Push notifications are enabled on this device.');
    } catch (err) {
      setPushMessage(pushPreferenceErrorMessage(err, 'Could not enable push notifications.'));
      if (!isExpectedPushEnableFailure(err)) {
        reportCaughtError(err, {
          source: 'notifications.push.enable',
          severity: 'warning',
          context: {
            isIos,
            isStandalone,
            notificationPermission: getNotificationPermission(),
          },
        });
      }
    } finally {
      setPushSaving(false);
    }
  };

  const handleDisablePush = async () => {
    if (!online) {
      setPushMessage(offlineWriteMessage('disable push notifications'));
      return;
    }
    setPushSaving(true);
    setPushMessage('');
    try {
      await disableWebPush();
      const nextPrefs = { ...pushPrefs, enabled: false };
      await updatePushPreferences(nextPrefs);
      setPushPrefs(nextPrefs);
      setPushMessage('Push notifications are disabled on this device.');
    } catch (err) {
      setPushMessage(pushPreferenceErrorMessage(err, 'Could not disable push notifications.'));
      reportCaughtError(err, {
        source: 'notifications.push.disable',
        severity: 'warning',
        context: {
          isIos,
          isStandalone,
          notificationPermission: getNotificationPermission(),
        },
      });
    } finally {
      setPushSaving(false);
    }
  };

  if (loading) return <p>Loading preferences...</p>;

  if (loadError && !hasLoadedRef.current) {
    return (
      <div className="notification-preferences">
        <p className="notification-preferences__message">{loadError}</p>
        <button className="button" type="button" onClick={() => fetchPrefs({ showLoader: true })} disabled={!online}>
          Retry
        </button>
        {!online && <p className="text-muted">{offlineWriteMessage('load communication preferences')}</p>}
      </div>
    );
  }

  const emailCategories = normalizeNotificationCategoryIds(prefs.categories);
  const pushCategories = normalizeNotificationCategoryIds(pushPrefs.categories);
  const visibleNotificationCategories = NOTIFICATION_CATEGORIES.filter((category) => (
    (category.id !== ROWING_CATEGORY_ID || showRowingCategory)
    && (category.id !== FAMILY_PROFILE_CATEGORY_ID || showFamilyProfileCategory)
  ));

  return (
    <div className="notification-preferences">
      <section className="notification-preferences__section">
        <div className="notification-preferences__header">
          <h2>Email notifications</h2>
          <label className="notification-preferences__toggle">
            <input
              type="checkbox"
              checked={prefs.receiveEmail}
              onChange={() => setPrefs(prev => ({ ...prev, receiveEmail: !prev.receiveEmail }))}
            />
            Receive email
          </label>
        </div>

        <fieldset className="notification-preferences__fieldset" disabled={!prefs.receiveEmail || emailSaving}>
          <legend>Email frequency</legend>
          <div className="notification-preferences__segmented">
            {['instant', 'daily', 'weekly', 'never'].map(option => (
              <label key={option}>
                <input
                  type="radio"
                  name="frequency"
                  value={option}
                  checked={prefs.frequency === option}
                  onChange={() => setPrefs(prev => ({ ...prev, frequency: option }))}
                />
                <span>{option.charAt(0).toUpperCase() + option.slice(1)}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="notification-preferences__fieldset" disabled={!prefs.receiveEmail || emailSaving}>
          <legend>Email interests</legend>
          <div className="notification-preferences__grid">
            {visibleNotificationCategories.map((category) => (
              <label className="notification-preferences__choice" key={`email-${category.id}`}>
                <input
                  type="checkbox"
                  checked={emailCategories.includes(category.id)}
                  onChange={() => toggleEmailCategory(category.id)}
                />
                <span>
                  <strong>{category.label}</strong>
                  <small>{category.description}</small>
                </span>
              </label>
            ))}
          </div>
        </fieldset>

        <button className="button" type="button" onClick={handleSave} disabled={!online || emailSaving}>
          {emailSaving ? 'Saving...' : 'Save email preferences'}
        </button>
        {emailMessage && <p className="notification-preferences__message">{emailMessage}</p>}
        {!online && <p className="text-muted">{offlineWriteMessage('save communication preferences')}</p>}
      </section>

      <section className="notification-preferences__section">
        <div className="notification-preferences__header">
          <h2>Push notifications</h2>
        </div>

        {!isStandalone ? (
          <p className="notification-preferences__message">
            Open the Member Nest app to manage push notifications.
          </p>
        ) : !pushReadiness.canEnable ? (
          <p className="notification-preferences__message">
            {pushReadiness.message}
          </p>
        ) : pushPrefs.enabled ? (
          <button className="button" type="button" onClick={handleDisablePush} disabled={pushSaving || !online}>
            {pushSaving ? 'Saving...' : 'Turn off push'}
          </button>
        ) : (
          <button className="button" type="button" onClick={handleEnablePush} disabled={pushSaving || !online}>
            {pushSaving ? 'Saving...' : 'Turn on push'}
          </button>
        )}

        {isStandalone && (
          <>
            <fieldset className="notification-preferences__fieldset" disabled={pushSaving || !online}>
              <legend>Push interests</legend>
              <div className="notification-preferences__grid">
                {visibleNotificationCategories.map((category) => (
                  <label className="notification-preferences__choice" key={`push-${category.id}`}>
                    <input
                      type="checkbox"
                      checked={pushCategories.includes(category.id)}
                      onChange={() => togglePushCategory(category.id)}
                    />
                    <span>
                      <strong>{category.label}</strong>
                      <small>{category.description}</small>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="notification-preferences__fieldset" disabled={pushSaving || !online}>
              <legend>Quiet hours</legend>
              <label className="notification-preferences__toggle">
                <input
                  type="checkbox"
                  checked={Boolean(pushPrefs.quietHours?.enabled)}
                  onChange={() => savePushPreferences({
                    quietHours: { enabled: !pushPrefs.quietHours?.enabled },
                  })}
                />
                Hold non-urgent push overnight
              </label>
              <div className="notification-preferences__time-grid">
                <label className="notification-preferences__time-field">
                  <span className="notification-preferences__time-label">Start</span>
                  <input
                    className="form-input notification-preferences__time-input"
                    type="time"
                    value={pushPrefs.quietHours?.start || '22:00'}
                    onChange={(event) => savePushPreferences({ quietHours: { start: event.target.value } })}
                  />
                </label>
                <label className="notification-preferences__time-field">
                  <span className="notification-preferences__time-label">End</span>
                  <input
                    className="form-input notification-preferences__time-input"
                    type="time"
                    value={pushPrefs.quietHours?.end || '08:00'}
                    onChange={(event) => savePushPreferences({ quietHours: { end: event.target.value } })}
                  />
                </label>
              </div>
              <div className="notification-preferences__timezone">
                <SearchableSelectDropdown
                  label="Timezone"
                  options={timezoneOptions}
                  value={pushPrefs.timezone || browserTimezone}
                  onChange={(timezone) => savePushPreferences({ timezone })}
                  placeholder="Choose timezone"
                  searchPlaceholder="Search timezone"
                  emptyMessage="No matching timezones."
                  disabled={pushSaving || !online}
                />
                <button
                  className="notification-preferences__device-timezone"
                  type="button"
                  onClick={() => savePushPreferences({ timezone: getBrowserTimezone() })}
                  disabled={pushSaving || !online}
                >
                  Use device timezone
                </button>
              </div>
            </fieldset>
          </>
        )}

        {pushMessage && <p className="notification-preferences__message">{pushMessage}</p>}
      </section>
    </div>
  );
}
